import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import { ROUTES } from '../../constants/routes';

const FEATURES = [
  {
    ico: '📍',
    title: 'Assigned in Under 5 Minutes',
    desc: 'As soon as you confirm, our nearest available Trustian is automatically dispatched to your location.',
  },
  {
    ico: '🗺️',
    title: 'Live GPS on the App',
    desc: "Watch your Trustian move toward you in real time. Push notification 5 minutes before arrival so you're ready.",
  },
  {
    ico: '📞',
    title: 'Call or WhatsApp Directly',
    desc: 'Reschedule, give directions, or just say you need 5 more minutes. Direct line to your Trustian, always.',
  },
];

const TrustianTracker = () => {
  const navigate = useNavigate();
  return (
    <Box sx={{ bgcolor: '#fff', py: { xs: 8, md: 13 } }}>
      <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 5, md: 8 },
            alignItems: 'center',
          }}
        >
          {/* Left text */}
          <Box>
            {/* Eyebrow */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.25,
                mb: 2,
                lineHeight: 1,
              }}
            >
              <Box sx={{ width: 24, height: '1.5px', bgcolor: 'var(--sage)' }} />
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: 'var(--sage)',
                  lineHeight: 1,
                }}
              >
                Trustian Tracking
              </Typography>
            </Box>

            <Typography
              className="serif"
              sx={{
                fontSize: { xs: 24, sm: 28, md: 32, lg: 36 },
                fontWeight: 500,
                fontStyle: 'italic',
                color: 'var(--ink)',
                lineHeight: 1.15,
                mb: 2.25,
              }}
            >
              Track your Trustian live
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: 14, md: 16 },
                lineHeight: 1.7,
                color: 'var(--stone)',
                fontWeight: 300,
                mb: 3.5,
              }}
            >
              We built live GPS tracking into diagnostics. You'll know exactly when your Trustian leaves, their ETA,
              and can call or WhatsApp them directly — all from the app.
            </Typography>

            {/* Features */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 3.5 }}>
              {FEATURES.map((f) => (
                <Box
                  key={f.title}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: 'var(--mint)',
                      border: '1.5px solid var(--sage-p)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 16,
                      flexShrink: 0,
                      lineHeight: 1,
                    }}
                  >
                    <Box component="span" sx={{ lineHeight: 1 }}>{f.ico}</Box>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: 'var(--ink)',
                        mb: 0.5,
                        lineHeight: 1.3,
                      }}
                    >
                      {f.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: 'var(--stone)',
                        fontWeight: 300,
                        lineHeight: 1.6,
                      }}
                    >
                      {f.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Guarantee box */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 2,
                bgcolor: 'var(--mint)',
                borderRadius: '14px',
                border: '1.5px solid var(--sage-p)',
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: 22,
                  lineHeight: 1,
                  color: 'var(--amber)',
                  flexShrink: 0,
                }}
              >
                ⚡
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: 'var(--forest)',
                    mb: 0.25,
                    lineHeight: 1.3,
                  }}
                >
                  60-Minute Arrival
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: 'var(--sage)', fontWeight: 400, lineHeight: 1.5 }}>
                  Late beyond 60 minutes? No home collection charge for that visit.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Right tracker shell */}
          <Box
            sx={{
              bgcolor: 'var(--forest)',
              borderRadius: '24px',
              p: { xs: 2.25, md: 3.25 },
              boxShadow: '0 28px 80px rgba(21,42,30,0.16)',
            }}
          >
            {/* Header row */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2.5,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#fff', mb: 0.5, lineHeight: 1.3 }}>
                  Ravi Kumar · Trustian
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1 }}>
                  Rating 4.9 ★ · Certified · 3.2 km away
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.625,
                  flexShrink: 0,
                  lineHeight: 1,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    bgcolor: 'var(--green)',
                    borderRadius: '50%',
                    animation: 'blink 1.4s ease-in-out infinite',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  component="span"
                  sx={{ fontSize: 11, fontWeight: 700, color: 'var(--green)', lineHeight: 1 }}
                >
                  LIVE
                </Typography>
              </Box>
            </Box>

            {/* Map mock */}
            <Box
              sx={{
                position: 'relative',
                bgcolor: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '16px',
                height: 160,
                overflow: 'hidden',
                mb: 2,
                backgroundImage:
                  'linear-gradient(rgba(200,237,216,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(200,237,216,0.04) 1px,transparent 1px)',
                backgroundSize: '28px 28px',
              }}
            >
              {/* Original full-width road restored. */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '16%',
                  width: '68%',
                  borderTop: '2px dashed rgba(106,174,128,0.35)',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: '15%',
                  top: 'calc(50% - 5px)',
                  width: 11,
                  height: 11,
                  bgcolor: 'rgba(255,255,255,0.25)',
                  borderRadius: '50%',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  right: '15%',
                  top: 'calc(50% - 7px)',
                  width: 14,
                  height: 14,
                  bgcolor: 'var(--sage-l)',
                  borderRadius: '50%',
                  boxShadow: '0 0 14px rgba(106,174,128,0.6)',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 'calc(50% - 14px)',
                  left: '15%',
                  color: '#EF4444',
                  display: 'inline-flex',
                  animation: 'ride 5s ease-in-out infinite',
                  // Destination dot is at right: 15%, width 14px → its
                  // center sits at calc(85% - 7px) from left. Bike icon
                  // is 28px wide, so its left edge at calc(85% - 21px)
                  // lands the bike's center exactly on the dot's center.
                  '@keyframes ride': {
                    '0%':         { left: '15%' },
                    '70%, 92%':   { left: 'calc(85% - 21px)' },
                    '93%, 100%':  { left: '15%' },
                  },
                }}
              >
                <TwoWheelerIcon sx={{ fontSize: 28, color: '#EF4444', display: 'block' }} />
              </Box>
            </Box>

            {/* ETA / Distance / Rating tiles */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 1,
                mb: 2,
              }}
            >
              {[
                { l: 'ETA', v: '12 min', hi: true },
                { l: 'Distance', v: '3.2 km' },
                { l: 'Rating', v: '4.9 ★' },
              ].map((c) => (
                <Box
                  key={c.l}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '12px',
                    px: 1,
                    py: 1.5,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    className="mono"
                    sx={{
                      fontSize: 10,
                      color: 'rgba(255,255,255,0.35)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.12em',
                      mb: 0.625,
                      lineHeight: 1,
                    }}
                  >
                    {c.l}
                  </Typography>
                  <Typography
                    className={c.hi ? 'serif' : ''}
                    sx={{
                      fontSize: c.hi ? 22 : 18,
                      fontWeight: 700,
                      color: c.hi ? 'var(--sage-l)' : '#fff',
                      lineHeight: 1,
                    }}
                  >
                    {c.v}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
              <Button
                sx={{
                  bgcolor: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 13,
                  borderRadius: '12px',
                  py: 1.25,
                  textTransform: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                <PhoneIcon sx={{ fontSize: 16, color: 'var(--sage-l)', display: 'block' }} />
                Call Trustian
              </Button>
              <Button
                onClick={() => navigate(ROUTES.TRACK)}
                sx={{
                  bgcolor: 'var(--amber)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 13,
                  borderRadius: '12px',
                  py: 1.25,
                  textTransform: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  '&:hover': { bgcolor: '#C77C1D' },
                }}
              >
                <WhatsAppIcon sx={{ fontSize: 16, display: 'block' }} />
                WhatsApp
              </Button>
            </Box>

            {/* Status rows */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.875, mt: 1.5 }}>
              {[
                ['Sample collection', 'Est. 8:22 AM'],
                ['Report on WhatsApp', 'By 2:30 PM'],
              ].map(([l, v]) => (
                <Box
                  key={l}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '12px',
                    px: 1.75,
                    py: 1.5,
                  }}
                >
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'rgba(255,255,255,0.6)' }}>
                    {l}
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--sage-l)' }}>
                    {v}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default TrustianTracker;
